import React, { useState, useEffect, useCallback } from 'react';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    InputAdornment,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Fab,
    Autocomplete,
    ButtonBase,
    Switch,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    InputBase,
} from '@mui/material';
import {
    Add,
    Remove,
    Close,
    DeleteOutline,
    CheckCircleOutline,
    LocalShipping,
    Undo as UndoIcon,
    CurrencyRupee,
    Receipt,
    AccessTime,
    Edit as EditIcon,
    Phone as PhoneIcon,
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    IosShare as ShareStoreIcon,
    WhatsApp as WhatsAppIcon,
    ContentCopy as CopyIcon,
    Check as CheckIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

import { orderAPI, businessAPI, notificationAPI } from '../api';
import ShareButtons from './ShareButtons';
import ProductImage from './common/ProductImage';
import { normalizeMobile, isInternationalMobile, getMobileError } from '../utils/mobile';

// ── Status display helpers ─────────────────────────────────────────────────

const STATUS_META = {
    created:   { label: 'Created',   color: 'default' },
    accepted:  { label: 'Accepted',  color: 'primary' },
    delivered: { label: 'Delivered', color: 'success' },
    cancelled: { label: 'Cancelled', color: 'error' },
};

const PAYMENT_META = {
    void:           { label: '—',           color: 'default' },
    due:            { label: 'Due',          color: 'warning' },
    paid_directly:  { label: 'Paid',         color: 'success' },
    invoice:        { label: 'Bill',         color: 'info' },
    pay_later:      { label: 'Pay Later',    color: 'warning' },
};

const SOURCE_LABELS = { public_store: 'Public Store', vendor: 'Vendor', customer_shop: 'Customer App' };

const STATUS_FILTERS = ['all', 'created', 'accepted', 'delivered', 'cancelled'];
const CLOSED_STATUS_FILTERS = ['all', 'delivered', 'cancelled'];
const PAYMENT_FILTERS = ['all', 'due', 'paid_directly', 'invoice', 'pay_later'];
const CLOSED_PAYMENT_FILTERS = ['all', 'paid_directly', 'invoice', 'pay_later'];

function fmt(amount) {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
}

function fmtDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function extractError(e, fallback = 'Something went wrong') {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (Array.isArray(detail)) return detail.map(d => d.msg || String(d)).join('; ');
    return typeof detail === 'string' ? detail : fallback;
}

function timeDiff(fromStr, toStr) {
    if (!fromStr || !toStr) return null;
    const mins = Math.round((new Date(toStr) - new Date(fromStr)) / 60000);
    if (mins < 1) return '<1m';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
}

// ── Main component ─────────────────────────────────────────────────────────

const OrdersPage = ({ businessId, business, customers, products, members, orderSettings, onCustomerCreated, initialScope = 'open', onRestored }) => {
    const isClosedMode = initialScope === 'closed';
    const [openOrders, setOpenOrders] = useState([]);
    const [openOffset, setOpenOffset] = useState(0);
    const [hasMoreOpen, setHasMoreOpen] = useState(true);
    const [closedOrders, setClosedOrders] = useState([]);
    const [showClosed, setShowClosed] = useState(false);
    const [closedOffset, setClosedOffset] = useState(0);
    const [hasMoreClosed, setHasMoreClosed] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [paymentFilter, setPaymentFilter] = useState('all');
    const [areaFilter, setAreaFilter] = useState('all');

    // Closed tab search + sort
    const [closedSearch, setClosedSearch] = useState('');
    const [closedSortBy, setClosedSortBy] = useState('updated_at');
    const [closedSortOrder, setClosedSortOrder] = useState('desc');
    const [debouncedClosedSearch, setDebouncedClosedSearch] = useState('');
    const closedSearchTimer = React.useRef(null);
    useEffect(() => {
        clearTimeout(closedSearchTimer.current);
        closedSearchTimer.current = setTimeout(() => setDebouncedClosedSearch(closedSearch), 300);
        return () => clearTimeout(closedSearchTimer.current);
    }, [closedSearch]);

    const [createOpen, setCreateOpen] = useState(false);
    const [editOrder, setEditOrder] = useState(null);
    const [paymentDialogOrder, setPaymentDialogOrder] = useState(null);
    const [statusDialogOrder, setStatusDialogOrder] = useState(null);
    const [cancelConfirmOrder, setCancelConfirmOrder] = useState(null);
    const [viewOrder, setViewOrder] = useState(null);
    const [shareStoreOpen, setShareStoreOpen] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [prepSummary, setPrepSummary] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    // Pending customer payment confirmations, keyed by order id
    const [orderClaims, setOrderClaims] = useState({});
    const [claimActionId, setClaimActionId] = useState(null);

    const OPEN_PAGE_SIZE = 50;
    const CLOSED_PAGE_SIZE = 10;

    const loadOrders = useCallback(async () => {
        try {
            setLoading(true);
            if (isClosedMode) {
                const res = await orderAPI.listOrders(businessId, {
                    scope: 'closed', limit: CLOSED_PAGE_SIZE, offset: 0,
                    sort_by: closedSortBy, sort_order: closedSortOrder,
                    ...(debouncedClosedSearch ? { search: debouncedClosedSearch } : {}),
                });
                setClosedOrders(res.data);
                setClosedOffset(res.data.length);
                setHasMoreClosed(res.data.length >= CLOSED_PAGE_SIZE);
                setShowClosed(true);
                setOpenOrders([]);
                setOpenOffset(0);
                setHasMoreOpen(true);
            } else {
                const res = await orderAPI.listOrders(businessId, { scope: 'open', limit: OPEN_PAGE_SIZE, offset: 0 });
                setOpenOrders(res.data);
                setOpenOffset(res.data.length);
                setHasMoreOpen(res.data.length >= OPEN_PAGE_SIZE);
                setClosedOrders([]);
                setClosedOffset(0);
                setShowClosed(false);
                setHasMoreClosed(true);
            }
        } catch (e) {
            setError('Failed to load orders');
        } finally {
            setLoading(false);
        }
    }, [businessId, isClosedMode, closedSortBy, closedSortOrder, debouncedClosedSearch]);

    const loadPrepSummary = useCallback(async () => {
        if (isClosedMode) return;
        try {
            const res = await orderAPI.getPrepSummary(businessId);
            setPrepSummary(res.data);
        } catch { /* non-critical */ }
    }, [businessId, isClosedMode]);

    const loadOrderClaims = useCallback(async () => {
        if (!businessId) return;
        try {
            const res = await notificationAPI.list(businessId, { status: 'pending', channel: 'orders' });
            const map = {};
            (res.data || []).forEach(c => { if (c.f_reference_id) map[c.f_reference_id] = c; });
            setOrderClaims(map);
        } catch { /* non-critical */ }
    }, [businessId]);

    const loadMoreOpen = useCallback(async (offset) => {
        try {
            setLoadingMore(true);
            const res = await orderAPI.listOrders(businessId, { scope: 'open', limit: OPEN_PAGE_SIZE, offset });
            const batch = res.data;
            setOpenOrders(prev => [...prev, ...batch]);
            setOpenOffset(offset + batch.length);
            if (batch.length < OPEN_PAGE_SIZE) setHasMoreOpen(false);
        } catch (e) {
            setError('Failed to load more orders');
        } finally {
            setLoadingMore(false);
        }
    }, [businessId]);

    const loadMoreClosed = useCallback(async (offset = 0) => {
        try {
            setLoadingMore(true);
            const res = await orderAPI.listOrders(businessId, {
                scope: 'closed', limit: CLOSED_PAGE_SIZE, offset,
                sort_by: closedSortBy, sort_order: closedSortOrder,
                ...(debouncedClosedSearch ? { search: debouncedClosedSearch } : {}),
            });
            const batch = res.data;
            if (offset === 0) setClosedOrders(batch);
            else setClosedOrders(prev => [...prev, ...batch]);
            setClosedOffset(offset + batch.length);
            if (batch.length < CLOSED_PAGE_SIZE) setHasMoreClosed(false);
        } catch (e) {
            setError('Failed to load more orders');
        } finally {
            setLoadingMore(false);
        }
    }, [businessId]);

    useEffect(() => { loadOrders(); loadPrepSummary(); loadOrderClaims(); }, [loadOrders, loadPrepSummary, loadOrderClaims]);

    const confirmClaim = async (order, claim) => {
        setClaimActionId(claim.f_id);
        try {
            await notificationAPI.accept(businessId, claim.f_id);
            await Promise.all([loadOrders(), loadOrderClaims()]);
            loadPrepSummary();
        } catch (e) {
            setError(extractError(e, 'Could not confirm payment'));
            await loadOrderClaims();
        } finally {
            setClaimActionId(null);
        }
    };

    const dismissClaim = async (claim) => {
        setClaimActionId(claim.f_id);
        try {
            await notificationAPI.reject(businessId, claim.f_id);
        } catch { /* ignore */ } finally {
            await loadOrderClaims();
            setClaimActionId(null);
        }
    };

    const handleAction = async (order, action, extra = {}) => {
        setActionLoading(true);
        try {
            await orderAPI.updateOrderStatus(businessId, order.f_id, { action, ...extra });
            await loadOrders();
            loadPrepSummary();
            if (action === 'uncancel' && onRestored) onRestored();
        } catch (e) {
            setError(extractError(e, 'Action failed'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleSetPayment = async (order, paymentStatus, paymentMethod = null) => {
        setActionLoading(true);
        try {
            await orderAPI.updateOrderStatus(businessId, order.f_id, {
                action: 'set_payment',
                payment_status: paymentStatus,
                payment_method: paymentMethod,
            });
            await loadOrders();
            setPaymentDialogOrder(null);
        } catch (e) {
            setError(extractError(e, 'Failed to set payment'));
        } finally {
            setActionLoading(false);
        }
    };

    // ── Derived ────────────────────────────────────────────────────────────

    const allOrders = (() => {
        if (!showClosed) return openOrders;
        const openIds = new Set(openOrders.map(o => o.f_id));
        return [...openOrders, ...closedOrders.filter(o => !openIds.has(o.f_id))];
    })();
    const totalCount = allOrders.length;

    const presentStatuses = new Set(allOrders.map(o => o.f_status));
    const presentPayments = new Set(allOrders.map(o => o.f_payment_status));
    const activeStatusFilters = (isClosedMode ? CLOSED_STATUS_FILTERS : STATUS_FILTERS)
        .filter(f => f === 'all' || presentStatuses.has(f));
    const activePaymentFilters = (isClosedMode ? CLOSED_PAYMENT_FILTERS : PAYMENT_FILTERS)
        .filter(f => f === 'all' || presentPayments.has(f));

    const allAreas = [...new Set(allOrders.map(o => o.customer_area).filter(Boolean))].sort();

    const filtered = allOrders.filter(o =>
        (statusFilter === 'all' || o.f_status === statusFilter) &&
        (paymentFilter === 'all' || o.f_payment_status === paymentFilter) &&
        (areaFilter === 'all' || o.customer_area === areaFilter)
    );

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <Box sx={{ pb: 10 }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Orders</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {totalCount}{(!isClosedMode && hasMoreOpen) || (isClosedMode && hasMoreClosed) ? '+' : ''} orders
                    </Typography>
                </Box>
                <Button
                    size="small" variant="outlined"
                    startIcon={<ShareStoreIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => setShareStoreOpen(true)}
                    sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: 2 }}
                >
                    Share Online Store
                </Button>
            </Box>

            {/* Closed tab: search + sort row */}
            {isClosedMode && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search by name, phone, order #…"
                        value={closedSearch}
                        onChange={(e) => setClosedSearch(e.target.value)}
                        sx={{ flex: '0 0 66%' }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                            endAdornment: closedSearch ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setClosedSearch('')}><ClearIcon fontSize="small" /></IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <FormControl size="small" sx={{ flex: '0 0 calc(34% - 8px)' }}>
                        <InputLabel>Sort</InputLabel>
                        <Select value={`${closedSortBy}:${closedSortOrder}`} label="Sort" onChange={(e) => {
                            const [sb, so] = e.target.value.split(':');
                            setClosedSortBy(sb);
                            setClosedSortOrder(so);
                        }}>
                            <MenuItem value="updated_at:desc">Last updated</MenuItem>
                            <MenuItem value="inserted_at:desc">Newest</MenuItem>
                            <MenuItem value="inserted_at:asc">Oldest</MenuItem>
                            <MenuItem value="total_amount:desc">High→Low</MenuItem>
                            <MenuItem value="total_amount:asc">Low→High</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}

            {/* Preparation panel — open tab only */}
            {!isClosedMode && prepSummary && prepSummary.product_totals?.length > 0 && (() => {
                const totals  = prepSummary.product_totals;
                const maxQty  = Math.max(...totals.map(p => p.total_qty), 1);
                return (
                    <Box sx={{ mb: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pt: 1, pb: 0.5 }}>
                            <Box sx={{ width: 3, height: 14, bgcolor: 'warning.main', borderRadius: 2 }} />
                            <Typography variant="caption" fontWeight={700} color="text.secondary"
                                sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', flex: 1 }}>
                                To Prepare
                            </Typography>
                            <Typography variant="caption" color="warning.dark" fontWeight={600}>
                                {prepSummary.active_orders} active order{prepSummary.active_orders !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        {totals.map((p, idx) => {
                            const pct = (p.total_qty / maxQty) * 100;
                            const qty = p.total_qty % 1 === 0 ? p.total_qty : p.total_qty.toFixed(1);
                            return (
                                <Box key={p.product_id}>
                                    {idx > 0 && <Divider />}
                                    <Box sx={{ px: 1.5, py: 0.75 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.4 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                                                {p.product_name}
                                            </Typography>
                                            <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ flexShrink: 0 }}>
                                                {qty} units
                                            </Typography>
                                        </Box>
                                        <Box sx={{ height: 5, borderRadius: 3, bgcolor: 'grey.100', overflow: 'hidden' }}>
                                            <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'warning.light', borderRadius: 3 }} />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                );
            })()}

            {/* Filter chips — status + payment + area */}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                {activeStatusFilters.map(f => (
                    <Chip
                        key={f}
                        label={f === 'all' ? 'All' : STATUS_META[f].label}
                        size="small"
                        color={statusFilter === f ? 'primary' : 'default'}
                        variant={statusFilter === f ? 'filled' : 'outlined'}
                        onClick={() => setStatusFilter(f)}
                        clickable
                        sx={{ height: 28, fontSize: '0.78rem', fontWeight: statusFilter === f ? 700 : 400 }}
                    />
                ))}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                {activePaymentFilters.map(f => {
                    const meta = PAYMENT_META[f];
                    return (
                        <Chip
                            key={f}
                            label={f === 'all' ? 'All pay' : meta.label}
                            size="small"
                            color={paymentFilter === f ? (f === 'all' ? 'primary' : meta.color) : 'default'}
                            variant={paymentFilter === f ? 'filled' : 'outlined'}
                            onClick={() => setPaymentFilter(f)}
                            clickable
                            sx={{ height: 28, fontSize: '0.78rem', fontWeight: paymentFilter === f ? 700 : 400 }}
                        />
                    );
                })}
                {allAreas.length > 0 && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                        <Chip
                            key="area-all"
                            label="All areas"
                            size="small"
                            color={areaFilter === 'all' ? 'primary' : 'default'}
                            variant={areaFilter === 'all' ? 'filled' : 'outlined'}
                            onClick={() => setAreaFilter('all')}
                            clickable
                            sx={{ height: 28, fontSize: '0.78rem', fontWeight: areaFilter === 'all' ? 700 : 400 }}
                        />
                        {allAreas.map(area => (
                            <Chip
                                key={area}
                                label={area}
                                size="small"
                                color={areaFilter === area ? 'secondary' : 'default'}
                                variant={areaFilter === area ? 'filled' : 'outlined'}
                                onClick={() => setAreaFilter(area)}
                                clickable
                                sx={{ height: 28, fontSize: '0.78rem', fontWeight: areaFilter === area ? 700 : 400 }}
                            />
                        ))}
                    </>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>
            )}

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Receipt sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        {statusFilter === 'all' ? 'No orders yet' : `No ${statusFilter} orders`}
                    </Typography>
                </Box>
            ) : (
                filtered.map(order => {
                    const sMeta = STATUS_META[order.f_status] || { label: order.f_status, color: 'default' };
                    const pMeta = PAYMENT_META[order.f_payment_status] || { label: order.f_payment_status, color: 'default' };

                    const isCancelled = order.f_status === 'cancelled';
                    const needsPayment = !isCancelled && (order.f_payment_status === 'due' || order.f_payment_status === 'pay_later');

                    const statusBorderColor = isCancelled ? '#d32f2f' : order.f_status === 'delivered' ? '#2e7d32' : order.f_status === 'accepted' ? '#2563EB' : '#bdbdbd';

                    // Primary action: only payment (status handled by dots) or restore for cancelled
                    let primaryAction = null;
                    if (needsPayment && !isClosedMode) primaryAction = { label: 'Record Payment', color: null, icon: <CurrencyRupee />, fn: () => setPaymentDialogOrder(order), sx: { bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' } } };
                    else if (isCancelled) primaryAction = { label: 'Restore Order', color: 'success', icon: <CheckCircleOutline />, fn: () => handleAction(order, 'uncancel'), sx: {} };

                    const orderDate = fmtDate(order.f_inserted_at);

                    const custName = order.customer_name || order.f_walkin_name || 'Walk-in';
                    const pLabel = PAYMENT_META[order.f_payment_status]?.label || order.f_payment_status;
                    const totalStr = parseFloat(order.f_total_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 });
                    const waText = [
                        `🏪 *${business?.f_name || 'Your Vendor'}*`,
                        '',
                        `🧾 Order #${order.f_order_number ?? order.f_id}`,
                        `👤 ${custName}`,
                        '',
                        `💰 Total: ₹${totalStr}   · ${pLabel}`,
                    ].join('\n');

                    return (
                        <Card key={order.f_id} variant="outlined" sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden', borderLeft: `4px solid ${statusBorderColor}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <CardContent sx={{ p: 0, pb: '0 !important' }}>
                                {/* Header strip: order ID + date | status chip + payment chip */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 0.75, bgcolor: `${statusBorderColor}14` }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
                                        <Typography variant="caption" color="text.disabled" noWrap>
                                            #{order.f_order_number ?? order.f_id}{orderDate ? ` · ${orderDate}` : ''}
                                        </Typography>
                                        {order.f_order_source && (
                                            <Typography variant="caption" noWrap sx={{ fontWeight: 700, color: order.f_order_source === 'vendor' ? 'text.secondary' : 'secondary.main' }}>
                                                {SOURCE_LABELS[order.f_order_source] || order.f_order_source}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        {order.f_fulfillment_type && (
                                            <Chip
                                                label={order.f_fulfillment_type === 'pickup' ? 'Pickup' : 'Delivery'}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700,
                                                    borderColor: order.f_fulfillment_type === 'pickup' ? '#7b1fa2' : '#0288D1',
                                                    color: order.f_fulfillment_type === 'pickup' ? '#7b1fa2' : '#0288D1',
                                                    '& .MuiChip-label': { px: 0.75 } }}
                                            />
                                        )}
                                        <Chip
                                            label={sMeta.label}
                                            size="small"
                                            color={sMeta.color}
                                            clickable
                                            deleteIcon={<ExpandMoreIcon sx={{ fontSize: '13px !important', ml: '-4px !important' }} />}
                                            onDelete={e => { e.stopPropagation(); setStatusDialogOrder(order); }}
                                            onClick={e => { e.stopPropagation(); setStatusDialogOrder(order); }}
                                            sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }}
                                        />
                                        {order.f_payment_status !== 'void' && (
                                            <Chip
                                                label={pMeta.label + (order.f_payment_method ? ' · ' + order.f_payment_method : '')}
                                                size="small"
                                                color={pMeta.color}
                                                variant={!isCancelled ? 'filled' : 'outlined'}
                                                clickable={!isCancelled}
                                                deleteIcon={!isCancelled ? <ExpandMoreIcon sx={{ fontSize: '13px !important', ml: '-4px !important' }} /> : undefined}
                                                onDelete={!isCancelled ? (e => { e.stopPropagation(); setPaymentDialogOrder(order); }) : undefined}
                                                onClick={!isCancelled ? (e => { e.stopPropagation(); setPaymentDialogOrder(order); }) : undefined}
                                                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }}
                                            />
                                        )}
                                    </Box>
                                </Box>

                                {/* Customer name + phone | amount */}
                                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 1, pb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                                        <Typography variant="body1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
                                            {custName}{order.customer_address_line1 ? ` · ${order.customer_address_line1}` : ''}
                                        </Typography>
                                        {order.customer_mobile && (
                                            <Tooltip title={`Call ${order.customer_mobile}`}>
                                                <IconButton
                                                    component="a"
                                                    href={`tel:${order.customer_mobile}`}
                                                    size="small"
                                                    sx={{ p: 0.5, color: 'primary.main', flexShrink: 0 }}
                                                >
                                                    <PhoneIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Typography fontWeight={700} sx={{ whiteSpace: 'nowrap', fontSize: '1.05rem', color: statusBorderColor }}>
                                        {fmt(order.f_total_amount)}
                                    </Typography>
                                </Box>

                                {/* Area + items + agent */}
                                <Box sx={{ px: 2, pb: 1 }}>
                                    {order.customer_area && (
                                        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.25 }}>
                                            📍 {order.customer_area}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                        {order.items.map(i => `${i.product_name} ×${parseFloat(i.f_quantity)}`).join(' · ')}
                                    </Typography>
                                    {order.f_notes && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25, whiteSpace: 'pre-line' }}>
                                            📝 {order.f_notes}
                                        </Typography>
                                    )}
                                    {order.agent_name && (
                                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.25 }}>
                                            👤 {order.agent_name}
                                        </Typography>
                                    )}
                                </Box>

                            </CardContent>

                            {/* Action area */}
                            <Box onClick={e => e.stopPropagation()}>
                                <Divider />

                                {/* Interactive timeline — segmented pill bar */}
                                {!isCancelled && (() => {
                                    const rank = { created: 1, accepted: 2, delivered: 3 }[order.f_status] || 1;
                                    const now = new Date().toISOString();
                                    const bornAsAccepted = order.accepted_at && order.f_inserted_at &&
                                        Math.abs(new Date(order.accepted_at) - new Date(order.f_inserted_at)) < 60000;
                                    const diffs = [
                                        bornAsAccepted ? null : (rank > 1 ? timeDiff(order.f_inserted_at, order.accepted_at) : null),
                                        rank === 2 ? timeDiff(order.accepted_at, now)
                                            : rank > 2 ? timeDiff(order.accepted_at, order.delivered_at) : null,
                                    ];

                                    const STEPS = [
                                        { label: 'Created',   color: '#616161', bg: '#61616118' },
                                        { label: 'Accepted',  color: '#1d4ed8', bg: '#1d4ed818' },
                                        { label: 'Delivered', color: '#15803d', bg: '#15803d18' },
                                    ];

                                    // Map target step index → sequence of backend actions needed
                                    const actionsToReach = [
                                        [],                                   // stay at created (no-op)
                                        rank === 1 ? ['accept'] : ['undeliver', 'unaccept'], // go to accepted
                                        rank === 1 ? ['accept', 'deliver'] : rank === 2 ? ['deliver'] : [], // go to delivered
                                    ];

                                    const handleStepTap = async (targetIdx, e) => {
                                        e.stopPropagation();
                                        if (actionLoading) return;
                                        const actions = actionsToReach[targetIdx];
                                        if (!actions.length) return;
                                        setActionLoading(true);
                                        try {
                                            for (const action of actions) {
                                                await orderAPI.updateOrderStatus(businessId, order.f_id, { action });
                                            }
                                            await loadOrders();
                                            loadPrepSummary();
                                        } catch (e) {
                                            setError(extractError(e, 'Action failed'));
                                        } finally {
                                            setActionLoading(false);
                                        }
                                    };

                                    const STEP_LABELS = ['Created', 'Accepted', 'Delivered'];
                                    return (
                                        <Box sx={{ px: 2, pt: 1, pb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            {STEPS.map((s, i) => {
                                                const stepRank = i + 1;
                                                const isDone = stepRank < rank;
                                                const isCurrent = stepRank === rank;
                                                const tappable = stepRank !== rank;
                                                return (
                                                    <Chip
                                                        key={i}
                                                        label={isDone ? `✓ ${STEP_LABELS[i]}` : STEP_LABELS[i]}
                                                        size="small"
                                                        clickable={tappable && !actionLoading}
                                                        onClick={tappable ? e => handleStepTap(i, e) : undefined}
                                                        sx={{
                                                            flex: 1,
                                                            height: 28,
                                                            fontWeight: isCurrent ? 700 : 400,
                                                            fontSize: '0.72rem',
                                                            bgcolor: isCurrent ? s.color : isDone ? 'grey.100' : 'transparent',
                                                            color: isCurrent ? '#fff' : isDone ? 'text.disabled' : s.color,
                                                            border: '1.5px solid',
                                                            borderColor: isCurrent ? s.color : isDone ? 'grey.200' : s.color + 'aa',
                                                            '& .MuiChip-label': { px: 1 },
                                                            '&:hover': tappable ? { opacity: 0.85 } : {},
                                                        }}
                                                    />
                                                );
                                            })}
                                            {diffs[1] && rank === 2 && (
                                                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                                                    {diffs[1]}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })()}

                                {/* Customer payment confirmation (inline) */}
                                {orderClaims[order.f_id] && (
                                    <Box sx={{ px: 1.5, pt: 1 }}>
                                        <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'success.light', border: '1px solid', borderColor: 'success.main' }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'success.dark' }}>
                                                💰 Customer says paid {fmt(orderClaims[order.f_id].f_amount)} · {orderClaims[order.f_id].f_payment_method || 'UPI'}
                                            </Typography>
                                            {orderClaims[order.f_id].f_message && (
                                                <Typography variant="caption" sx={{ display: 'block', color: 'success.dark' }}>
                                                    “{orderClaims[order.f_id].f_message}”
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                                                <Button
                                                    size="small" variant="contained" color="success"
                                                    disabled={claimActionId === orderClaims[order.f_id].f_id}
                                                    onClick={() => confirmClaim(order, orderClaims[order.f_id])}
                                                    sx={{ textTransform: 'none', fontWeight: 700, flex: 1, minHeight: 36 }}
                                                >
                                                    Confirm &amp; Record
                                                </Button>
                                                <Button
                                                    size="small" variant="outlined" color="inherit"
                                                    disabled={claimActionId === orderClaims[order.f_id].f_id}
                                                    onClick={() => dismissClaim(orderClaims[order.f_id])}
                                                    sx={{ textTransform: 'none', minHeight: 36 }}
                                                >
                                                    Dismiss
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}

                                {/* Primary action — large contextual button */}
                                {primaryAction && (
                                    <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color={primaryAction.color || undefined}
                                            startIcon={primaryAction.icon}
                                            disabled={actionLoading}
                                            onClick={primaryAction.fn}
                                            sx={{ height: 44, textTransform: 'none', fontWeight: 700, fontSize: '0.9rem', borderRadius: 1.5, color: 'white', ...primaryAction.sx }}
                                        >
                                            {primaryAction.label}
                                        </Button>
                                    </Box>
                                )}
                                {/* Secondary row: Edit + Cancel (always shown for non-cancelled orders) */}
                                {!isCancelled ? (
                                    <Box sx={{ display: 'flex', height: 40, px: 0.5, pb: 0.5, gap: 0.5 }}>
                                        <Button
                                            size="small" fullWidth
                                            startIcon={<EditIcon sx={{ fontSize: '15px !important' }} />}
                                            onClick={() => setEditOrder(order)}
                                            sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary', borderRadius: 1 }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="small" fullWidth
                                            disabled={actionLoading}
                                            onClick={() => setCancelConfirmOrder(order)}
                                            sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, color: 'error.main', borderRadius: 1 }}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ height: 4 }} />
                                )}

                                {/* Share buttons — bottom strip */}
                                <Divider />
                                <Box sx={{ px: 1.5, py: 1 }}>
                                    <ShareButtons
                                        phone={order.customer_mobile || ''}
                                        text={waText}
                                        getShareUrl={async () => {
                                            const res = await orderAPI.getOrderShareUrl(businessId, order.f_id);
                                            return res.data.share_url;
                                        }}
                                        onView={() => setViewOrder(order)}
                                        size="sm"
                                        label={false}
                                    />
                                </Box>
                            </Box>
                        </Card>
                    );
                })
            )}

            {/* Load more */}
            {!loading && (
                <Box sx={{ textAlign: 'center', mt: 1, mb: 1 }}>
                    {(!isClosedMode && hasMoreOpen) ? (
                        <Button size="small" variant="text" onClick={() => loadMoreOpen(openOffset)} disabled={loadingMore}>
                            {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                            Load more
                        </Button>
                    ) : (!isClosedMode && openOrders.length > 0 && !hasMoreOpen) ? (
                        <Typography variant="caption" color="text.secondary">All orders loaded</Typography>
                    ) : (isClosedMode && hasMoreClosed) ? (
                        <Button size="small" variant="text" onClick={() => loadMoreClosed(closedOffset)} disabled={loadingMore}>
                            {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                            Load more
                        </Button>
                    ) : (isClosedMode && closedOrders.length > 0) ? (
                        <Typography variant="caption" color="text.secondary">All orders loaded</Typography>
                    ) : null}
                </Box>
            )}

            {/* FAB to create order — hidden on closed tab */}
            {!isClosedMode && (
                <Fab
                    color="primary"
                    variant="extended"
                    sx={{ position: 'fixed', bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))', right: 'max(20px, calc(50% - 344px))', zIndex: 1200, gap: 0.75, px: 2, textTransform: 'none', fontWeight: 700 }}
                    onClick={() => setCreateOpen(true)}
                >
                    <Add /> Create Order
                </Fab>
            )}

            {/* Create Order Dialog */}
            {createOpen && (
                <CreateOrderDialog
                    businessId={businessId}
                    business={business}
                    customers={customers || []}
                    products={products || []}
                    members={members || []}
                    orderSettings={orderSettings}
                    onClose={() => setCreateOpen(false)}
                    onCreated={() => { setCreateOpen(false); loadOrders(); }}
                    onCustomerCreated={onCustomerCreated}
                />
            )}

            {/* Edit Order Dialog */}
            {editOrder && (
                <EditOrderDialog
                    businessId={businessId}
                    order={editOrder}
                    products={products || []}
                    members={members || []}
                    onClose={() => setEditOrder(null)}
                    onUpdated={() => { setEditOrder(null); loadOrders(); }}
                />
            )}

            {/* Status Dialog */}
            {statusDialogOrder && (
                <StatusActionDialog
                    order={statusDialogOrder}
                    loading={actionLoading}
                    onClose={() => setStatusDialogOrder(null)}
                    onAction={async (order, action) => {
                        setStatusDialogOrder(null);
                        await handleAction(order, action);
                    }}
                />
            )}

            {/* Payment Dialog */}
            {paymentDialogOrder && (
                <PaymentActionDialog
                    order={paymentDialogOrder}
                    business={business}
                    orderSettings={orderSettings}
                    isWalkin={!paymentDialogOrder.f_business_customer_id}
                    loading={actionLoading}
                    onClose={() => setPaymentDialogOrder(null)}
                    onSelect={handleSetPayment}
                />
            )}

            {/* Share Store bottom sheet */}
            {shareStoreOpen && (() => {
                const isEnabled = !!business?.f_public_store_enabled;
                const storeUrl = `${window.location.origin}/store/${businessId}`;
                const bizName = business?.f_name || 'our store';
                const waUrl = `https://wa.me/?text=${encodeURIComponent(`Order from *${bizName}* 🛒\n${storeUrl}`)}`;
                return (
                    <BottomSheet open onClose={() => setShareStoreOpen(false)} title="Share Store" subtitle={isEnabled ? 'Send your store link to customers' : 'Public store is not enabled'}>
                        {!isEnabled && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                Public store is disabled. Go to <strong>Edit Business</strong> and turn on <em>Online Public Store</em> to share your store link.
                            </Alert>
                        )}
                        {isEnabled && (
                            <>
                                <SheetOption
                                    colorKey="success"
                                    icon={<WhatsAppIcon />}
                                    label="Share on WhatsApp"
                                    sub="Opens WhatsApp with your store link"
                                    onClick={() => { window.open(waUrl, '_blank'); setShareStoreOpen(false); }}
                                />
                                <SheetOption
                                    colorKey="primary"
                                    icon={<CopyIcon />}
                                    label={linkCopied ? 'Link Copied!' : 'Copy Link'}
                                    sub={storeUrl}
                                    onClick={() => {
                                        navigator.clipboard.writeText(storeUrl).catch(() => {});
                                        setLinkCopied(true);
                                        setTimeout(() => { setLinkCopied(false); setShareStoreOpen(false); }, 1500);
                                    }}
                                />
                            </>
                        )}
                        <Button variant="text" fullWidth onClick={() => setShareStoreOpen(false)}
                            sx={{ mt: 0.5, color: 'text.secondary', textTransform: 'none' }}>
                            Cancel
                        </Button>
                    </BottomSheet>
                );
            })()}

            {/* Order Receipt Dialog */}
            {viewOrder && (
                <OrderReceiptDialog
                    order={viewOrder}
                    business={business}
                    orderSettings={orderSettings}
                    onClose={() => setViewOrder(null)}
                />
            )}

            {/* Cancel Confirmation Dialog */}
            {cancelConfirmOrder && (
                <Dialog open onClose={() => setCancelConfirmOrder(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>Cancel Order?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Are you sure you want to cancel the order for <strong>{cancelConfirmOrder.customer_name || 'Walk-in'}</strong> of <strong>{fmt(cancelConfirmOrder.f_total_amount)}</strong>?
                        </Typography>
                        {cancelConfirmOrder.f_status === 'delivered' && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                This order is already delivered. Cancelling will set payment status to void.
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 2, pb: 2 }}>
                        <Button onClick={() => setCancelConfirmOrder(null)} disabled={actionLoading}>No, keep it</Button>
                        <Button
                            variant="contained" color="error" disabled={actionLoading}
                            onClick={async () => {
                                await handleAction(cancelConfirmOrder, 'cancel');
                                setCancelConfirmOrder(null);
                            }}
                        >
                            {actionLoading ? <CircularProgress size={18} color="inherit" /> : 'Yes, cancel order'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

// ── Order Receipt Dialog ───────────────────────────────────────────────────

function OrderReceiptDialog({ order, business, orderSettings, onClose }) {
    const biz = business || {};
    const terms = orderSettings?.f_terms_conditions;
    const custName = order.customer_name || order.f_walkin_name || 'Walk-in';
    const pMeta = PAYMENT_META[order.f_payment_status] || { label: order.f_payment_status, color: 'default' };
    const sMeta = STATUS_META[order.f_status] || { label: order.f_status, color: 'default' };

    const orderDate = order.f_inserted_at
        ? new Date(order.f_inserted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        : '';

    const items = order.items || [];
    const subtotal = parseFloat(order.f_subtotal) || items.reduce((s, i) => s + (parseFloat(i.f_line_total) || 0), 0);
    const orderDiscount = parseFloat(order.f_discount_amount) || 0;   // order-level discount (per-item discounts are already inside line totals)
    const total = parseFloat(order.f_total_amount) || 0;
    const advance = parseFloat(order.f_advance_payment) || 0;
    const netDue = total - advance;
    // Advance is informational and always shown when collected. Balance Due only applies to
    // unpaid orders — a paid order has no balance even if an advance was recorded earlier.
    const showAdvance = advance > 0;
    const showBalance = advance > 0 && (order.f_payment_status === 'due' || order.f_payment_status === 'pay_later');

    const statusBorderColor = order.f_status === 'delivered' ? '#2e7d32'
        : order.f_status === 'accepted' ? '#2563EB'
        : order.f_status === 'cancelled' ? '#d32f2f'
        : '#bdbdbd';

    return (
        <Dialog open onClose={onClose} {...APP_DIALOG}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">Order Receipt</Typography>
                <IconButton onClick={onClose} aria-label="Close"><Close /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: '#f5f5f5' }}>
                <Box sx={{ py: 2, px: 1 }}>
                    <Box sx={{
                        maxWidth: 480, mx: 'auto',
                        bgcolor: 'background.paper',
                        border: '1px solid', borderColor: 'divider',
                        borderLeft: `4px solid ${statusBorderColor}`,
                        borderRadius: 1,
                        overflow: 'hidden',
                    }}>
                        {/* Business header */}
                        <Box sx={{ textAlign: 'center', py: 2, px: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ letterSpacing: 0.5 }}>{biz.f_name || 'Your Business'}</Typography>
                            {[biz.f_address1, biz.f_address2].filter(Boolean).map((line, i) => (
                                <Typography key={i} variant="caption" display="block" sx={{ opacity: 0.9 }}>{line}</Typography>
                            ))}
                            {[biz.f_city, biz.f_state, biz.f_pincode].filter(Boolean).length > 0 && (
                                <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                                    {[biz.f_city, biz.f_state, biz.f_pincode].filter(Boolean).join(', ')}
                                </Typography>
                            )}
                            {biz.f_phone && (
                                <Typography variant="body2" display="block" sx={{ mt: 0.75, fontWeight: 'bold', bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1, px: 1.5, py: 0.5, display: 'inline-block', fontSize: '0.95rem', letterSpacing: 0.5 }}>
                                    📞 {biz.f_phone}
                                </Typography>
                            )}
                            <Typography variant="caption" display="block" sx={{ opacity: 0.75, mt: 0.5 }}>
                                Order Date: {orderDate}
                            </Typography>
                        </Box>

                        <Divider sx={{ borderStyle: 'dashed' }} />

                        {/* Customer + order info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.5, alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary">Customer</Typography>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{custName}</Typography>
                                {order.customer_mobile && (
                                    <Typography variant="caption" color="text.secondary" display="block">{order.customer_mobile}</Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">Order <strong>#{order.f_order_number ?? order.f_id}</strong></Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Chip size="small" label={sMeta.label} color={sMeta.color} sx={{ mb: 0.5, fontSize: '0.72rem', height: 22 }} />
                                {order.f_payment_status !== 'void' && (
                                    <Box>
                                        <Chip size="small" label={pMeta.label + (order.f_payment_method ? ' · ' + order.f_payment_method : '')} color={pMeta.color} variant="filled" sx={{ fontSize: '0.72rem', height: 22 }} />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ borderStyle: 'dashed', mx: 2 }} />

                        {/* Items table */}
                        <Box sx={{ px: 2, py: 1 }}>
                            <Table size="small" padding="none">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ py: 0.75, fontWeight: 'bold', fontSize: '0.72rem', borderBottom: '1px solid', borderColor: 'divider' }}>Product</TableCell>
                                        <TableCell align="right" sx={{ py: 0.75, fontWeight: 'bold', fontSize: '0.72rem', width: 32, borderBottom: '1px solid', borderColor: 'divider' }}>Qty</TableCell>
                                        <TableCell align="right" sx={{ py: 0.75, fontWeight: 'bold', fontSize: '0.72rem', width: 64, borderBottom: '1px solid', borderColor: 'divider' }}>Rate</TableCell>
                                        <TableCell align="right" sx={{ py: 0.75, fontWeight: 'bold', fontSize: '0.72rem', width: 68, borderBottom: '1px solid', borderColor: 'divider' }}>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item, idx) => {
                                        const qty = parseFloat(item.f_quantity) || 0;
                                        const price = parseFloat(item.f_unit_price) || 0;
                                        const disc = parseFloat(item.f_discount) || 0;
                                        const lineTotal = parseFloat(item.f_line_total) || Math.max(0, qty * price - qty * disc);
                                        return (
                                            <TableRow key={item.f_id || idx}>
                                                <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>
                                                    {item.product_name || '—'}
                                                    {disc > 0 && (
                                                        <Typography component="span" variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                                                            (−₹{disc}/unit)
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', color: 'text.secondary' }}>{qty}</TableCell>
                                                <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', color: 'text.secondary' }}>₹{price.toFixed(2)}</TableCell>
                                                <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 500 }}>
                                                    ₹{lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {items.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary', fontSize: '0.8rem' }}>No items</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>

                        <Divider sx={{ borderStyle: 'dashed', mx: 2 }} />

                        {/* Totals */}
                        <Box sx={{ px: 2, py: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                                <Typography variant="body2">
                                    ₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                            </Box>
                            {orderDiscount > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" color="success.main">
                                        Discount{order.f_discount_type === 'percent' && parseFloat(order.f_discount_value) > 0 ? ` (${parseFloat(order.f_discount_value)}%)` : ''}
                                    </Typography>
                                    <Typography variant="body2" color="success.main" fontWeight="bold">
                                        −₹{orderDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            )}
                            <Divider sx={{ my: 0.75 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: showAdvance ? 0.5 : 0 }}>
                                <Typography variant="subtitle2" fontWeight="bold">Total</Typography>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                            </Box>
                            {showAdvance && (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: showBalance ? 0.5 : 0 }}>
                                        <Typography variant="body2" color="success.main">Advance Paid</Typography>
                                        <Typography variant="body2" color="success.main" fontWeight="bold">
                                            ₹{advance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                    {showBalance && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="error.main" fontWeight="bold">Balance Due</Typography>
                                            <Typography variant="body2" color="error.main" fontWeight="bold">
                                                ₹{netDue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>

                        {/* Notes */}
                        {order.f_notes && (
                            <>
                                <Divider sx={{ borderStyle: 'dashed' }} />
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Notes
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.25 }}>{order.f_notes}</Typography>
                                </Box>
                            </>
                        )}

                        {/* Terms & Conditions */}
                        {terms && (
                            <>
                                <Divider sx={{ borderStyle: 'dashed' }} />
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Terms &amp; Conditions
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{terms}</Typography>
                                </Box>
                            </>
                        )}

                        {/* Footer */}
                        <Box sx={{ textAlign: 'center', py: 1.5, px: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                            {biz.f_upi_id && (order.f_payment_status === 'due' || order.f_payment_status === 'pay_later') && (() => {
                                // Keep the VPA's @ literal — encoding it to %40 makes some UPI apps fail to resolve the payee
                                const upiLink = `upi://pay?pa=${encodeURIComponent(biz.f_upi_id).replace(/%40/g, '@')}&pn=${encodeURIComponent(biz.f_name || '')}&am=${total.toFixed(2)}&cu=INR&tn=Order%23${order.f_order_number ?? order.f_id}`;
                                return (
                                    <Box
                                        component="a"
                                        href={upiLink}
                                        sx={{ display: 'inline-block', mb: 1, bgcolor: 'success.main', color: '#fff', borderRadius: 1, px: 1.5, py: 0.5, textDecoration: 'none', fontWeight: 'bold', fontSize: '0.82rem' }}
                                    >
                                        💳 Pay Now — ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Box>
                                );
                            })()}
                            <Typography variant="caption" color="text.disabled" display="block">
                                Powered by vendora88.com
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

// ── Create Order Dialog ────────────────────────────────────────────────────

const EMPTY_ITEM = () => ({
    _key: Math.random(),
    f_business_product_id: '',
    f_quantity: 1,
    f_unit_price: '',
    f_discount: 0,
    f_line_total: 0,
});

// ── Item form helpers shared by Create & Edit ─────────────────────────────

function useItemForm(initialItems = [EMPTY_ITEM()]) {
    const [items, setItems] = useState(initialItems);

    const updateItem = (key, field, value) => {
        setItems(prev => prev.map(item => {
            if (item._key !== key) return item;
            const updated = { ...item, [field]: value };
            if (['f_unit_price', 'f_discount', 'f_quantity'].includes(field)) {
                const qty = parseFloat(field === 'f_quantity' ? value : updated.f_quantity) || 0;
                const price = parseFloat(field === 'f_unit_price' ? value : updated.f_unit_price) || 0;
                const disc = parseFloat(field === 'f_discount' ? value : updated.f_discount) || 0;
                updated.f_line_total = Math.max(0, qty * price - qty * disc);
            }
            return updated;
        }));
    };

    return { items, setItems, updateItem };
}

async function fetchProductDefaults(businessId, key, productId, setItems) {
    try {
        const res = await orderAPI.getProductDefaults(businessId, productId);
        const { unit_price, discount } = res.data;
        setItems(prev => prev.map(item => {
            if (item._key !== key) return item;
            const qty = parseFloat(item.f_quantity) || 1;
            const price = parseFloat(unit_price) || 0;
            const disc = parseFloat(discount) || 0;
            return { ...item, f_business_product_id: productId, f_unit_price: String(price), f_discount: String(disc), f_line_total: Math.max(0, qty * price - qty * disc) };
        }));
    } catch (e) { /* user can fill manually */ }
}

function ItemsEditor({ businessId, items, setItems, updateItem, products }) {
    return (
        <>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, letterSpacing: '0.05em' }}>ITEMS</Typography>
            {items.map((item) => (
                <Box key={item._key} sx={{ border: '1px solid', borderColor: item.f_business_product_id ? 'primary.light' : 'divider', borderRadius: 2, p: 1.25, mb: 1.25, bgcolor: 'background.paper' }}>
                    {/* Row 1: Product + Qty + Delete */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        <Autocomplete
                            size="small" sx={{ flex: 1, minWidth: 0 }}
                            options={products.filter(p => p.f_is_available)}
                            getOptionLabel={p => p.f_name || ''}
                            value={products.find(p => p.f_id === item.f_business_product_id) || null}
                            onChange={(_, val) => {
                                if (val) fetchProductDefaults(businessId, item._key, val.f_id, setItems);
                                else updateItem(item._key, 'f_business_product_id', '');
                            }}
                            renderInput={params => <TextField {...params} label="Product" />}
                        />
                        <TextField size="small" sx={{ width: 64, flexShrink: 0 }} label="Qty" type="number"
                            value={item.f_quantity} onChange={e => updateItem(item._key, 'f_quantity', e.target.value)}
                            inputProps={{ min: 1, step: 1, inputMode: 'decimal' }} />
                        <IconButton size="small" color="error" sx={{ p: 0.75, flexShrink: 0 }}
                            disabled={items.length === 1}
                            onClick={() => setItems(prev => prev.filter(i => i._key !== item._key))}>
                            <DeleteOutline />
                        </IconButton>
                    </Box>
                    {/* Row 2: Price + Disc + Line total */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField size="small" sx={{ flex: 1 }} label="Price" value={item.f_unit_price}
                            onChange={e => updateItem(item._key, 'f_unit_price', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            type="number" inputProps={{ min: 0, step: 0.5, inputMode: 'decimal' }} />
                        <TextField size="small" sx={{ flex: 1 }} label="Disc" value={item.f_discount}
                            onChange={e => updateItem(item._key, 'f_discount', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            type="number" inputProps={{ min: 0, step: 0.5, inputMode: 'decimal' }} />
                        {item.f_line_total > 0 && (
                            <Typography variant="body2" fontWeight="bold" color="primary.main" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                = {fmt(item.f_line_total)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}
            <Button size="small" variant="outlined" fullWidth startIcon={<Add />}
                onClick={() => setItems(prev => [...prev, EMPTY_ITEM()])}
                sx={{ mb: 2 }}>
                Add item
            </Button>
        </>
    );
}

function TotalSummary({ items, discountType, discountValue, setDiscountType, setDiscountValue, deliveryCharge = 0 }) {
    const subtotal = items.reduce((s, i) => s + (parseFloat(i.f_line_total) || 0), 0);
    const discAmt = (() => {
        const v = parseFloat(discountValue) || 0;
        return v ? (discountType === 'percent' ? subtotal * v / 100 : v) : 0;
    })();
    const delivery = parseFloat(deliveryCharge) || 0;
    const total = Math.max(0, subtotal - discAmt) + delivery;
    return (
        <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ width: 120 }}>
                    <InputLabel>Discount</InputLabel>
                    <Select label="Discount" value={discountType} onChange={e => setDiscountType(e.target.value)}>
                        <MenuItem value="flat">Flat ₹</MenuItem>
                        <MenuItem value="percent">Percent %</MenuItem>
                    </Select>
                </FormControl>
                <TextField size="small" sx={{ width: 100 }} label="Value" type="number"
                    value={discountValue} onChange={e => setDiscountValue(e.target.value)}
                    inputProps={{ min: 0, step: 1, inputMode: 'decimal' }} />
            </Box>
            <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Subtotal</Typography>
                    <Typography variant="body2">{fmt(subtotal)}</Typography>
                </Box>
                {discAmt > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'success.main' }}>
                        <Typography variant="body2">Discount</Typography>
                        <Typography variant="body2">− {fmt(discAmt)}</Typography>
                    </Box>
                )}
                {delivery > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Delivery</Typography>
                        <Typography variant="body2">{fmt(delivery)}</Typography>
                    </Box>
                )}
                <Divider sx={{ my: 0.75 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Total</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary.main">{fmt(total)}</Typography>
                </Box>
            </Box>
        </>
    );
}

// ── Product Catalog Picker ─────────────────────────────────────────────────

function ProductCatalogPicker({ products, cart, setCart, priceOverrides, setPriceOverrides }) {
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [editingQtyPid, setEditingQtyPid] = useState(null);
    const [qtyInputVal, setQtyInputVal] = useState('');

    const categories = [...new Set(
        products.filter(p => p.f_business_product_category).map(p => p.f_business_product_category)
    )].sort();

    const visible = categoryFilter === 'all'
        ? products
        : products.filter(p => p.f_business_product_category === categoryFilter);

    const toggleExpand = (pid) => setExpandedIds(prev => {
        const next = new Set(prev);
        if (next.has(pid)) next.delete(pid); else next.add(pid);
        return next;
    });

    const setQty = (pid, qty) => setCart(prev => {
        if (qty <= 0) { const n = { ...prev }; delete n[pid]; return n; }
        return { ...prev, [pid]: qty };
    });

    const getPriceVal = (p) => priceOverrides[p.f_id]?.price !== undefined
        ? priceOverrides[p.f_id].price
        : String(parseFloat(p.current_price) || 0);

    const getDiscVal = (p) => priceOverrides[p.f_id]?.discount !== undefined
        ? priceOverrides[p.f_id].discount
        : String(parseFloat(p.current_discount) || 0);

    const setOverride = (pid, field, val) => setPriceOverrides(prev => ({
        ...prev,
        [pid]: { ...(prev[pid] || {}), [field]: val },
    }));

    return (
        <>
            {/* Category chips */}
            {categories.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.25 }}>
                    <Chip label="All" size="small" clickable
                        onClick={() => setCategoryFilter('all')}
                        color={categoryFilter === 'all' ? 'primary' : 'default'}
                        variant={categoryFilter === 'all' ? 'filled' : 'outlined'}
                        sx={{ height: 28, fontSize: '0.78rem' }} />
                    {categories.map(cat => (
                        <Chip key={cat} label={cat} size="small" clickable
                            onClick={() => setCategoryFilter(cat)}
                            color={categoryFilter === cat ? 'primary' : 'default'}
                            variant={categoryFilter === cat ? 'filled' : 'outlined'}
                            sx={{ height: 28, fontSize: '0.78rem' }} />
                    ))}
                </Box>
            )}

            {/* Product cards */}
            {visible.map(product => {
                const pid = product.f_id;
                const qty = cart[pid] || 0;
                const isExpanded = expandedIds.has(pid);
                const priceVal = getPriceVal(product);
                const discVal = getDiscVal(product);
                const netPrice = Math.max(0, (parseFloat(priceVal) || 0) - (parseFloat(discVal) || 0));
                const lineTotal = netPrice * qty;
                const minQty = parseFloat(product.f_min_order_qty) || 1;
                const stepQty = parseFloat(product.f_increment_order_qty) || 1;

                return (
                    <Card key={pid} variant="outlined" sx={{
                        mb: 0.75, borderRadius: 2,
                        ...(qty > 0 ? { borderColor: 'primary.main', borderWidth: 1.5 } : {}),
                    }}>
                        <Box sx={{ px: 1.5, py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ProductImage src={product.f_image_url} name={product.f_name}
                                    fontSize="0.9rem" sx={{ width: 40, height: 40, borderRadius: 1, flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>{product.f_name}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" fontWeight={700}
                                            color={qty > 0 ? 'primary.main' : 'text.secondary'}>
                                            {fmt(netPrice)}
                                        </Typography>
                                        {lineTotal > 0 && (
                                            <Typography variant="caption" color="text.disabled">· {fmt(lineTotal)}</Typography>
                                        )}
                                        <IconButton size="small" onClick={() => toggleExpand(pid)}
                                            sx={{ p: 0.25, ml: 0.25, color: isExpanded ? 'primary.main' : 'text.disabled' }}>
                                            <EditIcon sx={{ fontSize: 13 }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                                {qty === 0 ? (
                                    <Button variant="outlined" size="small" onClick={() => setQty(pid, minQty)}
                                        sx={{ minWidth: 60, height: 32, textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}>
                                        Add
                                    </Button>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', border: '1.5px solid', borderColor: 'primary.main', borderRadius: 1.5, overflow: 'hidden' }}>
                                        <IconButton size="small" onClick={() => setQty(pid, qty - stepQty < minQty ? 0 : qty - stepQty)}
                                            sx={{ color: 'primary.main', borderRadius: 0, p: 0.5 }}>
                                            <Remove sx={{ fontSize: 14 }} />
                                        </IconButton>
                                        {editingQtyPid === pid ? (
                                            <InputBase
                                                autoFocus
                                                value={qtyInputVal}
                                                onChange={e => setQtyInputVal(e.target.value)}
                                                onBlur={() => {
                                                    const n = parseFloat(qtyInputVal);
                                                    if (isNaN(n) || n <= 0) { setQty(pid, 0); }
                                                    else {
                                                        const rounded = Math.round(n / stepQty) * stepQty;
                                                        setQty(pid, rounded < minQty ? 0 : rounded);
                                                    }
                                                    setEditingQtyPid(null);
                                                }}
                                                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                                                inputProps={{ inputMode: 'decimal', style: { textAlign: 'center', fontWeight: 700, fontSize: '0.88rem', padding: 0 } }}
                                                sx={{ width: 44 }}
                                            />
                                        ) : (
                                            <Typography
                                                onClick={() => { setEditingQtyPid(pid); setQtyInputVal(String(qty)); }}
                                                sx={{ minWidth: 28, px: 0.5, textAlign: 'center', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', userSelect: 'none' }}>
                                                {qty}
                                            </Typography>
                                        )}
                                        <IconButton size="small" onClick={() => setQty(pid, qty + stepQty)}
                                            sx={{ color: 'primary.main', borderRadius: 0, p: 0.5 }}>
                                            <Add sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                            {isExpanded && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                                    <TextField size="small" label="Price (₹)" type="number"
                                        value={priceVal}
                                        onChange={e => setOverride(pid, 'price', e.target.value)}
                                        inputProps={{ min: 0, step: 0.5, inputMode: 'decimal' }}
                                        sx={{ flex: 1 }} />
                                    <TextField size="small" label="Discount (₹)" type="number"
                                        value={discVal}
                                        onChange={e => setOverride(pid, 'discount', e.target.value)}
                                        inputProps={{ min: 0, step: 0.5, inputMode: 'decimal' }}
                                        sx={{ flex: 1 }} />
                                </Box>
                            )}
                        </Box>
                    </Card>
                );
            })}
        </>
    );
}

// ── Create Order Dialog ────────────────────────────────────────────────────

const CreateOrderDialog = ({ businessId, business, customers, products, members, orderSettings, onClose, onCreated, onCustomerCreated }) => {
    const hasBilling = business?.f_business_mode === 'both';
    const [customerInput, setCustomerInput] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [deliveryStatus, setDeliveryStatus] = useState('created');
    const [paymentStatus, setPaymentStatus] = useState('due');
    const [paymentMethod, setPaymentMethod] = useState('Cash'); // used when paymentStatus === 'paid_directly'
    const [fulfillmentType, setFulfillmentType] = useState('delivery');
    const [deliveryCharge, setDeliveryCharge] = useState('');
    const [advancePayment, setAdvancePayment] = useState('');
    const [agentMemberId, setAgentMemberId] = useState('');
    const [cart, setCart] = useState({});
    const [priceOverrides, setPriceOverrides] = useState({});
    const [discountType, setDiscountType] = useState('flat');
    const [discountValue, setDiscountValue] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [newCustName, setNewCustName] = useState('');
    const [newCustMobile, setNewCustMobile] = useState('');
    const [newCustAreaId, setNewCustAreaId] = useState(null);
    const [addCustLoading, setAddCustLoading] = useState(false);
    const [addCustError, setAddCustError] = useState('');
    const [skipAddCustomer, setSkipAddCustomer] = useState(false);
    const [dialogAreas, setDialogAreas] = useState([]);

    // Seed defaults from the vendor-order settings once available
    useEffect(() => {
        if (!orderSettings) return;
        setDeliveryStatus(orderSettings.f_vendor_default_order_status || 'created');
        const ps = orderSettings.f_vendor_default_payment_status || 'due';
        setPaymentStatus(ps === 'add_to_monthly_bill' ? 'invoice' : ps);
        setPaymentMethod(orderSettings.f_vendor_default_payment_method || 'Cash');
        const ff = orderSettings.f_vendor_default_fulfillment_type || 'delivery';
        setFulfillmentType(ff);
        setDeliveryCharge(ff !== 'pickup' && orderSettings.f_vendor_default_delivery_charge != null
            ? String(orderSettings.f_vendor_default_delivery_charge) : '');
    }, [orderSettings]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        businessAPI.getBusinessAreas(businessId)
            .then(r => setDialogAreas(r.data || []))
            .catch(e => console.error('Failed to load areas for dialog:', e));
    }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Includes the Owner row (sorted first by the API): a vendor-booked order defaults to the
    // owner's own member row, so the dropdown has to be able to select it back.
    const agents = (members || []).filter(m => m.f_is_active);
    const activeProducts = (products || []).filter(p => p.f_is_available && !p.f_is_archived && p.f_selling_mode !== 1);

    // Derive cart items for TotalSummary and advance calc
    const cartItemsForSummary = activeProducts.filter(p => (cart[p.f_id] || 0) > 0).map(p => {
        const price = parseFloat(priceOverrides[p.f_id]?.price !== undefined ? priceOverrides[p.f_id].price : p.current_price) || 0;
        const disc  = parseFloat(priceOverrides[p.f_id]?.discount !== undefined ? priceOverrides[p.f_id].discount : p.current_discount) || 0;
        return { f_line_total: Math.max(0, (price - disc) * (cart[p.f_id] || 0)) };
    });
    const subtotal = cartItemsForSummary.reduce((s, i) => s + i.f_line_total, 0);
    const discAmt = (() => { const v = parseFloat(discountValue) || 0; return v ? (discountType === 'percent' ? subtotal * v / 100 : v) : 0; })();
    const deliveryChargeNum = fulfillmentType === 'pickup' ? 0 : (parseFloat(deliveryCharge) || 0);
    const grandTotal = Math.max(0, subtotal - discAmt) + deliveryChargeNum;

    const activeCustomers = (customers || []).filter(c => !c.f_is_archived);
    const filteredCount = customerInput
        ? activeCustomers.filter(o =>
            o.f_name?.toLowerCase().includes(customerInput.toLowerCase()) ||
            o.f_mobile?.includes(customerInput)
          ).length
        : 0;
    const showAddPrompt = customerInput.length >= 3 && !selectedCustomer && filteredCount === 0 && !skipAddCustomer;

    // Auto-fill agent from customer's assigned agent
    useEffect(() => {
        if (selectedCustomer?.f_agent_id) {
            const match = agents.find(a => a.f_id === selectedCustomer.f_agent_id);
            setAgentMemberId(match ? match.f_id : '');
        } else {
            setAgentMemberId('');
        }
    }, [selectedCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep name field in sync with whatever is typed in the autocomplete while panel is open
    useEffect(() => {
        if (showAddPrompt) {
            const isPhone = /^\d{7,}$/.test(customerInput.trim());
            setNewCustName(isPhone ? '' : customerInput.trim());
            setNewCustMobile(prev => prev || (isPhone ? customerInput.trim() : ''));
            setAddCustError('');
        } else {
            setNewCustName('');
            setNewCustMobile('');
            setNewCustAreaId(null);
            setAddCustError('');
        }
    }, [showAddPrompt, customerInput]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async () => {
        const custId = selectedCustomer?.f_id || null;
        const walkin = !selectedCustomer && customerInput.trim() ? customerInput.trim() : null;
        if (!custId && !walkin) { setError('Enter a customer or walk-in name'); return; }
        const cartEntries = activeProducts.filter(p => (cart[p.f_id] || 0) > 0);
        if (cartEntries.length === 0) { setError('Add at least one product'); return; }
        setLoading(true); setError('');
        try {
            await orderAPI.createOrder(businessId, {
                f_business_customer_id: custId,
                f_walkin_name: walkin,
                f_initial_status: deliveryStatus,
                f_initial_payment_status: paymentStatus,
                f_payment_method: paymentStatus === 'paid_directly' ? paymentMethod : null,
                f_fulfillment_type: fulfillmentType,
                f_delivery_charge: deliveryChargeNum,
                f_advance_payment: parseFloat(advancePayment) || 0,
                f_agent_id: agentMemberId || null,
                items: cartEntries.map(p => ({
                    f_business_product_id: p.f_id,
                    f_quantity: cart[p.f_id],
                    f_unit_price: parseFloat(priceOverrides[p.f_id]?.price !== undefined ? priceOverrides[p.f_id].price : p.current_price) || 0,
                    f_discount: parseFloat(priceOverrides[p.f_id]?.discount !== undefined ? priceOverrides[p.f_id].discount : p.current_discount) || 0,
                })),
                f_discount_type: discountType,
                f_discount_value: parseFloat(discountValue) || 0,
                f_notes: notes.trim() || null,
            });
            onCreated();
        } catch (e) {
            setError(extractError(e, 'Failed to create order'));
        } finally {
            setLoading(false);
        }
    };

    const isWalkin = !selectedCustomer;
    const bizMode = business?.f_business_mode || 'subscription';
    const billingMode = orderSettings?.f_billing_mode || 'none';
    const showBilling = !isWalkin && (bizMode === 'subscription' || bizMode === 'both' || billingMode.includes('monthly'));
    const showDues = !isWalkin && billingMode.includes('pay_later');
    const pmtOptions = [
        { key: 'due',       status: 'due',            label: 'Due',          color: 'warning' },
        { key: 'paid_upi',   status: 'paid_directly', method: 'UPI',   label: 'Paid — UPI',   color: 'success' },
        { key: 'paid_cash',  status: 'paid_directly', method: 'Cash',  label: 'Paid — Cash',  color: 'success' },
        { key: 'paid_other', status: 'paid_directly', method: 'Other', label: 'Paid — Other', color: 'success' },
        ...(showDues ? [{ key: 'pay_later', status: 'pay_later', label: 'Move to Dues Page', color: 'default' }] : []),
        ...(showBilling ? [{ key: 'invoice',  status: 'invoice',  label: 'Monthly Bills',     color: 'info'    }] : []),
    ];

    // Reset to 'due' if current payment status is invalid for walk-in or a disabled option
    useEffect(() => {
        if (isWalkin && (paymentStatus === 'pay_later' || paymentStatus === 'invoice')) {
            setPaymentStatus('due');
        }
        if (!showDues && paymentStatus === 'pay_later') {
            setPaymentStatus('due');
        }
        if (!showBilling && paymentStatus === 'invoice') {
            setPaymentStatus('due');
        }
    }, [isWalkin, showBilling, showDues]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Dialog open onClose={onClose} {...APP_DIALOG}
            PaperProps={{ sx: { ...APP_DIALOG.PaperProps.sx, display: 'flex', flexDirection: 'column',
                                height: { xs: '100%', sm: 'calc(100% - 64px)' } } }}>
            <Box sx={{ ...APP_DIALOG_TITLE_SX, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <Typography variant="h6" fontWeight={700} color="white">New Order</Typography>
                <IconButton onClick={onClose} aria-label="Close"><Close /></IconButton>
            </Box>
            <DialogContent sx={{ px: 2, pt: 0, pb: 2, flex: 1, overflowY: 'auto' }}>
                {error && <Alert severity="error" sx={{ mt: 1.5, mb: 0 }} onClose={() => setError('')}>{error}</Alert>}

                {/* ── Customer ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1.25 }}>
                    <Box sx={{ width: 3, height: 16, bgcolor: 'primary.main', borderRadius: 2 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Customer</Typography>
                </Box>

                {/* Customer / walk-in */}
                <Autocomplete
                    freeSolo size="small"
                    options={activeCustomers}
                    getOptionLabel={c => typeof c === 'string' ? c : `${c.f_name || ''}${c.f_mobile ? ' · ' + c.f_mobile : ''}`}
                    filterOptions={(opts, { inputValue }) =>
                        inputValue
                            ? opts.filter(o => {
                                const q = inputValue.toLowerCase();
                                return o.f_name?.toLowerCase().includes(q) ||
                                       o.f_mobile?.includes(inputValue) ||
                                       o.f_address_line1?.toLowerCase().includes(q);
                              }).slice(0, 8)
                            : []
                    }
                    value={selectedCustomer}
                    inputValue={customerInput}
                    onInputChange={(_, val, reason) => {
                        if (reason === 'reset' && !val) return; // blur without selection — keep current text
                        setCustomerInput(val);
                        if (reason !== 'reset') { setSelectedCustomer(null); setSkipAddCustomer(false); }
                    }}
                    onChange={(_, val) => {
                        if (val && typeof val === 'object') { setSelectedCustomer(val); setCustomerInput(val.f_name || ''); }
                        else { setSelectedCustomer(null); }
                    }}
                    renderInput={params => (
                        <TextField {...params} label="Customer / Walk-in name or mobile *" size="small" fullWidth
                            helperText={
                                selectedCustomer
                                    ? `Existing customer${selectedCustomer.f_mobile ? ' · ' + selectedCustomer.f_mobile : ''}`
                                    : customerInput && !showAddPrompt
                                        ? 'Will be recorded as walk-in'
                                        : ''
                            }
                        />
                    )}
                    sx={{ mb: showAddPrompt ? 1 : 2 }}
                />

                {/* No-match: inline add-customer panel */}
                {showAddPrompt && (
                    <Box sx={{
                        border: '1px solid', borderColor: 'primary.main',
                        borderRadius: 1.5, bgcolor: 'rgba(37,99,235,0.04)',
                        p: 1.5, mb: 2,
                    }}>
                        <Typography variant="caption" color="primary.main" fontWeight={700}
                            sx={{ display: 'block', mb: 1.25, letterSpacing: '0.05em' }}>
                            NEW CUSTOMER
                        </Typography>
                        {addCustError && (
                            <Alert severity="error" sx={{ mb: 1.25, py: 0.5 }} onClose={() => setAddCustError('')}>
                                {addCustError}
                            </Alert>
                        )}
                        <TextField
                            size="small" fullWidth label="Name *"
                            value={newCustName}
                            onChange={e => setNewCustName(e.target.value)}
                            sx={{ mb: 1.25 }}
                        />
                        <TextField
                            size="small" fullWidth label="Mobile *"
                            value={newCustMobile}
                            onChange={e => setNewCustMobile(e.target.value)}
                            inputProps={{ inputMode: 'tel' }}
                            error={!!getMobileError(newCustMobile)}
                            helperText={getMobileError(newCustMobile) || (() => {
                                const norm = normalizeMobile(newCustMobile);
                                return norm && norm !== newCustMobile.replace(/\D/g, '') ? `Will be saved as ${norm}` : '';
                            })()}
                            sx={{ mb: 1.25 }}
                        />
                        {dialogAreas.filter(a => a.f_is_active).length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Area</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {dialogAreas.filter(a => a.f_is_active).map(a => (
                                        <Chip
                                            key={a.f_id}
                                            label={a.f_name}
                                            size="small"
                                            variant={newCustAreaId === a.f_id ? 'filled' : 'outlined'}
                                            color={newCustAreaId === a.f_id ? 'primary' : 'default'}
                                            onClick={() => setNewCustAreaId(prev => prev === a.f_id ? null : a.f_id)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <Button
                            variant="contained" fullWidth
                            disabled={addCustLoading || !newCustName.trim() || !newCustMobile.trim() || !!getMobileError(newCustMobile)}
                            onClick={async () => {
                                const mobileErr = getMobileError(newCustMobile);
                                if (mobileErr) { setAddCustError(mobileErr); return; }
                                const normalizedMobile = normalizeMobile(newCustMobile);
                                setAddCustLoading(true); setAddCustError('');
                                try {
                                    const res = await businessAPI.createCustomer({
                                        f_business_id: businessId,
                                        f_name: newCustName.trim(),
                                        f_mobile: normalizedMobile,
                                        ...(newCustAreaId ? { f_area_id: newCustAreaId } : {}),
                                    });
                                    const newCust = res.data;
                                    setSelectedCustomer(newCust);
                                    setCustomerInput(newCust.f_name || '');
                                    if (onCustomerCreated) onCustomerCreated();
                                } catch (e) {
                                    setAddCustError(extractError(e, 'Failed to create customer'));
                                } finally {
                                    setAddCustLoading(false);
                                }
                            }}
                        >
                            {addCustLoading ? <CircularProgress size={18} color="inherit" /> : 'Add & Select'}
                        </Button>
                        <Button
                            variant="outlined" fullWidth
                            onClick={() => setSkipAddCustomer(true)}
                            sx={{ mt: 1, textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                    </Box>
                )}

                {/* ── Items ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, mb: 1.25 }}>
                    <Box sx={{ width: 3, height: 16, bgcolor: 'primary.main', borderRadius: 2 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Items</Typography>
                    {cartItemsForSummary.length > 0 && (
                        <Box sx={{ ml: 'auto', px: 1, py: 0.25, borderRadius: 5, bgcolor: 'primary.main' }}>
                            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.68rem' }}>
                                {cartItemsForSummary.length} in cart
                            </Typography>
                        </Box>
                    )}
                </Box>
                <ProductCatalogPicker
                    products={activeProducts}
                    cart={cart} setCart={setCart}
                    priceOverrides={priceOverrides} setPriceOverrides={setPriceOverrides}
                />

                {/* ── Delivery ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, mb: 1.25 }}>
                    <Box sx={{ width: 3, height: 16, bgcolor: 'warning.dark', borderRadius: 2 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Delivery</Typography>
                </Box>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: fulfillmentType === 'pickup' ? 0 : 1.5 }}>
                        {[{ v: 'delivery', l: 'Home Delivery' }, { v: 'pickup', l: 'Self Pickup' }].map(o => (
                            <Chip
                                key={o.v}
                                label={o.l}
                                clickable
                                onClick={() => setFulfillmentType(o.v)}
                                color={fulfillmentType === o.v ? 'warning' : 'default'}
                                variant={fulfillmentType === o.v ? 'filled' : 'outlined'}
                                sx={{ flex: 1, height: 38, fontSize: '0.85rem', fontWeight: fulfillmentType === o.v ? 700 : 500 }}
                            />
                        ))}
                    </Box>
                    {fulfillmentType !== 'pickup' && (
                        <TextField
                            size="small"
                            label="Delivery charge"
                            type="number"
                            value={deliveryCharge}
                            onChange={e => setDeliveryCharge(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            inputProps={{ min: 0, step: 1, inputMode: 'decimal' }}
                            helperText="Added as a Delivery Charge line — clear for none"
                            fullWidth
                        />
                    )}
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="body2" fontWeight={600}>Already {fulfillmentType === 'pickup' ? 'picked up' : 'delivered'}?</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {deliveryStatus === 'delivered' ? 'Order is complete' : 'Still to be fulfilled'}
                            </Typography>
                        </Box>
                        <Switch
                            checked={deliveryStatus === 'delivered'}
                            onChange={e => setDeliveryStatus(e.target.checked ? 'delivered' : 'accepted')}
                            color="success"
                        />
                    </Box>
                </Box>

                {/* ── Bill ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 1.25 }}>
                    <Box sx={{ width: 3, height: 16, bgcolor: 'primary.main', borderRadius: 2 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Bill</Typography>
                </Box>
                <TotalSummary items={cartItemsForSummary} discountType={discountType} discountValue={discountValue} setDiscountType={setDiscountType} setDiscountValue={setDiscountValue} deliveryCharge={deliveryChargeNum} />

                {/* ── Payment ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 1.25 }}>
                    <Box sx={{ width: 3, height: 16, bgcolor: 'success.main', borderRadius: 2 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Payment</Typography>
                </Box>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {pmtOptions.map(opt => {
                            const sel = opt.method
                                ? (paymentStatus === 'paid_directly' && paymentMethod === opt.method)
                                : paymentStatus === opt.status;
                            return (
                                <Chip
                                    key={opt.key}
                                    label={opt.label}
                                    clickable
                                    onClick={() => { setPaymentStatus(opt.status); if (opt.method) setPaymentMethod(opt.method); }}
                                    color={sel ? opt.color : 'default'}
                                    variant={sel ? 'filled' : 'outlined'}
                                    sx={{ height: 36, fontSize: '0.85rem', fontWeight: sel ? 700 : 400 }}
                                />
                            );
                        })}
                    </Box>
                    {(paymentStatus === 'due' || paymentStatus === 'pay_later') && (() => {
                        const adv = parseFloat(advancePayment) || 0;
                        return (
                            <TextField
                                size="small" fullWidth
                                label="Advance collected (optional)"
                                type="number"
                                value={advancePayment}
                                onChange={e => setAdvancePayment(e.target.value)}
                                inputProps={{ min: 0, step: 1, inputMode: 'decimal' }}
                                helperText={adv > 0 ? `Net due: ${fmt(Math.max(0, grandTotal - adv))}` : 'Leave blank if no advance taken'}
                                sx={{ mt: 1.5 }}
                            />
                        );
                    })()}
                </Box>

                {/* ── More ── */}
                {agents.length > 0 && (
                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Assign to agent (optional)</InputLabel>
                        <Select label="Assign to agent (optional)" value={agentMemberId} onChange={e => setAgentMemberId(e.target.value)}>
                            <MenuItem value="">— None —</MenuItem>
                            {agents.map(a => <MenuItem key={a.f_id} value={a.f_id}>{a.f_name || a.f_mobile}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}

                <TextField size="small" fullWidth multiline rows={2} label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} />
            </DialogContent>
            <Box sx={{ px: 2, pt: 1.5, pb: 3, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                    variant="contained" fullWidth size="large" onClick={handleSubmit}
                    disabled={loading}
                    sx={{ height: 50, fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: 2, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: (!loading && cartItemsForSummary.length > 0) ? 'space-between' : 'center' }}
                >
                    {loading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        <>
                            <Box component="span">Create Order</Box>
                            {cartItemsForSummary.length > 0 && <Box component="span">{fmt(grandTotal)}</Box>}
                        </>
                    )}
                </Button>
                <Button
                    variant="text" fullWidth onClick={onClose} disabled={loading}
                    sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                    Cancel
                </Button>
            </Box>
        </Dialog>
    );
};

// ── Edit Order Dialog ──────────────────────────────────────────────────────

const EditOrderDialog = ({ businessId, order, products, members, onClose, onUpdated }) => {
    // Initialise cart + priceOverrides from the existing order items (use stored prices)
    const initialCart = {};
    const initialOverrides = {};
    (order.items || []).forEach(i => {
        initialCart[i.f_business_product_id] = parseFloat(i.f_quantity);
        initialOverrides[i.f_business_product_id] = {
            price:    String(parseFloat(i.f_unit_price)),
            discount: String(parseFloat(i.f_discount)),
        };
    });
    const [cart, setCart] = useState(initialCart);
    const [priceOverrides, setPriceOverrides] = useState(initialOverrides);
    const [discountType, setDiscountType] = useState(order.f_discount_type || 'flat');
    const [discountValue, setDiscountValue] = useState(String(order.f_discount_value || ''));
    const [advancePayment, setAdvancePayment] = useState(String(order.f_advance_payment > 0 ? order.f_advance_payment : ''));
    const [notes, setNotes] = useState(order.f_notes || '');
    const [agentMemberId, setAgentMemberId] = useState(order.f_agent_id || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Includes the Owner row (sorted first by the API): a vendor-booked order defaults to the
    // owner's own member row, so the dropdown has to be able to select it back.
    const agents = (members || []).filter(m => m.f_is_active);

    // Products available in the picker + any original items that may now be archived.
    // Mirror the New Order picker exactly: available, not archived, and not plans-only.
    const activeProducts = (products || []).filter(p => p.f_is_available && !p.f_is_archived && p.f_selling_mode !== 1);
    const archivedInOrder = (order.items || [])
        .filter(i => !activeProducts.find(p => p.f_id === i.f_business_product_id))
        .map(i => ({ f_id: i.f_business_product_id, f_name: i.product_name, current_price: i.f_unit_price, current_discount: i.f_discount, f_business_product_category: null, f_is_available: true, f_is_archived: true }));
    const pickerProducts = [...activeProducts, ...archivedInOrder];

    const cartItemsForSummary = pickerProducts.filter(p => (cart[p.f_id] || 0) > 0).map(p => {
        const price = parseFloat(priceOverrides[p.f_id]?.price !== undefined ? priceOverrides[p.f_id].price : p.current_price) || 0;
        const disc  = parseFloat(priceOverrides[p.f_id]?.discount !== undefined ? priceOverrides[p.f_id].discount : p.current_discount) || 0;
        return { f_line_total: Math.max(0, (price - disc) * (cart[p.f_id] || 0)) };
    });

    const handleSubmit = async () => {
        const cartEntries = pickerProducts.filter(p => (cart[p.f_id] || 0) > 0);
        if (cartEntries.length === 0) { setError('Add at least one product'); return; }
        setLoading(true); setError('');
        try {
            await orderAPI.updateOrder(businessId, order.f_id, {
                items: cartEntries.map(p => ({
                    f_business_product_id: p.f_id,
                    f_quantity: cart[p.f_id],
                    f_unit_price: parseFloat(priceOverrides[p.f_id]?.price !== undefined ? priceOverrides[p.f_id].price : p.current_price) || 0,
                    f_discount:  parseFloat(priceOverrides[p.f_id]?.discount !== undefined ? priceOverrides[p.f_id].discount : p.current_discount) || 0,
                })),
                f_discount_type: discountType,
                f_discount_value: parseFloat(discountValue) || 0,
                f_advance_payment: parseFloat(advancePayment) || 0,
                f_notes: notes.trim() || null,
                f_agent_id: agentMemberId || null,
            });
            onUpdated();
        } catch (e) {
            setError(extractError(e, 'Failed to update order'));
        } finally {
            setLoading(false);
        }
    };

    const sMeta = STATUS_META[order.f_status] || { label: order.f_status, color: 'default' };
    const pMeta = PAYMENT_META[order.f_payment_status] || { label: order.f_payment_status, color: 'default' };
    const custName = order.customer_name || order.f_walkin_name || 'Walk-in';

    return (
        <Dialog open onClose={onClose} {...APP_DIALOG}
            PaperProps={{ sx: { ...APP_DIALOG.PaperProps.sx, display: 'flex', flexDirection: 'column',
                                height: { xs: '100%', sm: 'calc(100% - 64px)' } } }}>

            {/* Coloured header */}
            <Box sx={{ ...APP_DIALOG_TITLE_SX, px: 2, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h6" fontWeight={700} color="white" sx={{ lineHeight: 1.2 }}>
                            Edit Order
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mt: 0.25 }}>
                            #{order.f_order_number ?? order.f_id} · {custName}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                        <Chip label={sMeta.label} size="small" color={sMeta.color}
                            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.18)', color: 'white', '& .MuiChip-label': { px: 0.75 } }} />
                        {order.f_payment_status !== 'void' && (
                            <Chip label={pMeta.label + (order.f_payment_method ? ' · ' + order.f_payment_method : '')} size="small" color={pMeta.color}
                                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.18)', color: 'white', '& .MuiChip-label': { px: 0.75 } }} />
                        )}
                        <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5, ml: 0.5 }}><Close /></IconButton>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ px: 2, pt: 0, pb: 2, flex: 1, overflowY: 'auto' }}>
                {error && <Alert severity="error" sx={{ mt: 1.5, mb: 0 }} onClose={() => setError('')}>{error}</Alert>}

                {/* ── Agent ── */}
                {agents.length > 0 && (
                    <FormControl size="small" fullWidth sx={{ mt: 2, mb: 0 }}>
                        <InputLabel>Assign to agent (optional)</InputLabel>
                        <Select label="Assign to agent (optional)" value={agentMemberId} onChange={e => setAgentMemberId(e.target.value)}>
                            <MenuItem value="">— None —</MenuItem>
                            {agents.map(a => <MenuItem key={a.f_id} value={a.f_id}>{a.f_name || a.f_mobile}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}

                {/* ── Items ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1.25 }}>
                    <Box sx={{ width: 3, height: 16, bgcolor: 'primary.main', borderRadius: 2 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Items</Typography>
                </Box>
                <ProductCatalogPicker
                    products={pickerProducts}
                    cart={cart} setCart={setCart}
                    priceOverrides={priceOverrides} setPriceOverrides={setPriceOverrides}
                />
                <TotalSummary items={cartItemsForSummary} discountType={discountType} discountValue={discountValue} setDiscountType={setDiscountType} setDiscountValue={setDiscountValue} />

                {/* Advance (only when payment is due/pay_later) */}
                {(order.f_payment_status === 'due' || order.f_payment_status === 'pay_later') && (() => {
                    const subtotal = cartItemsForSummary.reduce((s, i) => s + (parseFloat(i.f_line_total) || 0), 0);
                    const dv = parseFloat(discountValue) || 0;
                    const dAmt = dv ? (discountType === 'percent' ? subtotal * dv / 100 : dv) : 0;
                    const total = Math.max(0, subtotal - dAmt);
                    const adv = parseFloat(advancePayment) || 0;
                    return (
                        <TextField
                            size="small" fullWidth
                            label="Advance collected (optional)"
                            type="number"
                            value={advancePayment}
                            onChange={e => setAdvancePayment(e.target.value)}
                            inputProps={{ min: 0, step: 1, inputMode: 'decimal' }}
                            helperText={adv > 0 ? `Net due: ${fmt(Math.max(0, total - adv))}` : 'Leave blank if no advance taken'}
                            sx={{ mb: 2 }}
                        />
                    );
                })()}

                {/* ── Notes ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1.25 }}>
                    <Box sx={{ width: 3, height: 16, bgcolor: 'primary.main', borderRadius: 2 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Notes</Typography>
                </Box>
                <TextField
                    size="small" fullWidth multiline rows={2}
                    label="Notes (optional)"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                />
            </DialogContent>

            {/* Bottom action bar */}
            <Box sx={{ px: 2, pt: 1.5, pb: 3, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                    variant="contained" fullWidth size="large"
                    onClick={handleSubmit} disabled={loading}
                    sx={{ height: 48, fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: 2 }}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
                </Button>
                <Button
                    variant="text" fullWidth onClick={onClose} disabled={loading}
                    sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                    Cancel
                </Button>
            </Box>
        </Dialog>
    );
};

// ── Shared bottom-sheet helpers ────────────────────────────────────────────

const SHEET_COLORS = {
    primary: { main: '#2563EB', light: 'rgba(37,99,235,0.07)', border: 'rgba(37,99,235,0.22)' },
    success: { main: '#2e7d32', light: 'rgba(46,125,50,0.07)',  border: 'rgba(46,125,50,0.22)'  },
    warning: { main: '#ed6c02', light: 'rgba(237,108,2,0.07)', border: 'rgba(237,108,2,0.22)'  },
    error:   { main: '#d32f2f', light: 'rgba(211,47,47,0.07)', border: 'rgba(211,47,47,0.22)'  },
    grey:    { main: '#757575', light: 'rgba(117,117,117,0.06)',border: 'rgba(117,117,117,0.2)' },
};

const BottomSheet = ({ open, onClose, title, subtitle, children }) => (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, pb: 1 } }}
    >
        <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>}
        </Box>
        <Box sx={{ px: 2 }}>{children}</Box>
    </Dialog>
);

const SheetOption = ({ colorKey = 'grey', icon, label, sub, isCurrent, disabled, onClick }) => {
    const c = SHEET_COLORS[colorKey] || SHEET_COLORS.grey;
    return (
        <ButtonBase
            onClick={onClick}
            disabled={disabled}
            sx={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 1.75,
                p: 1.5, mb: 1, borderRadius: 2, textAlign: 'left',
                border: '1.5px solid', borderColor: isCurrent ? c.main : c.border,
                bgcolor: isCurrent ? c.light : 'background.paper',
                opacity: disabled && !isCurrent ? 0.5 : 1,
                transition: 'all 0.15s',
            }}
        >
            <Box sx={{ width: 46, height: 46, borderRadius: '50%', bgcolor: isCurrent ? c.main : c.light, border: `2px solid ${c.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {React.cloneElement(icon, { sx: { fontSize: 22, color: isCurrent ? 'white' : c.main } })}
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight={700} sx={{ color: c.main, lineHeight: 1.3 }}>
                    {label}{isCurrent ? ' ✓' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">{sub}</Typography>
            </Box>
        </ButtonBase>
    );
};

// ── Status action dialog ───────────────────────────────────────────────────

const STATUS_ACTIONS = {
    created:   [{ action: 'accept',    colorKey: 'primary', icon: <CheckCircleOutline />, label: 'Accept Order',       sub: 'Confirm and move to accepted'     }],
    accepted:  [{ action: 'deliver',   colorKey: 'success', icon: <LocalShipping />,      label: 'Mark as Delivered',  sub: 'Confirm delivery is done'          },
                { action: 'unaccept',  colorKey: 'warning', icon: <UndoIcon />,            label: 'Back to Created',    sub: 'Undo acceptance'                  }],
    delivered: [{ action: 'undeliver', colorKey: 'warning', icon: <UndoIcon />,            label: 'Undeliver',          sub: 'Mark as not yet delivered'         }],
    cancelled: [{ action: 'uncancel',  colorKey: 'success', icon: <CheckCircleOutline />, label: 'Restore Order',      sub: 'Move back to accepted'             }],
};

const StatusActionDialog = ({ order, loading, onClose, onAction }) => {
    const current = STATUS_META[order.f_status] || { label: order.f_status, color: 'default' };
    const options = STATUS_ACTIONS[order.f_status] || [];
    return (
        <BottomSheet open onClose={onClose} title="Order Status"
            subtitle={<>Currently: <Chip label={current.label} size="small" color={current.color} sx={{ ml: 0.5, height: 20, fontSize: '0.72rem' }} /></>}
        >
            {options.map(opt => (
                <SheetOption
                    key={opt.action}
                    colorKey={opt.colorKey}
                    icon={opt.icon}
                    label={opt.label}
                    sub={opt.sub}
                    disabled={loading}
                    onClick={() => onAction(order, opt.action)}
                />
            ))}
            <Button variant="text" fullWidth onClick={onClose} disabled={loading}
                sx={{ mt: 0.5, color: 'text.secondary', textTransform: 'none' }}>
                Cancel
            </Button>
        </BottomSheet>
    );
};

// ── Payment action dialog ──────────────────────────────────────────────────

const PaymentActionDialog = ({ order, business, orderSettings, isWalkin, loading, onClose, onSelect }) => {
    const bizMode = business?.f_business_mode || 'subscription';
    const billingMode = orderSettings?.f_billing_mode || 'none';
    const showBilling = !isWalkin && (bizMode === 'subscription' || bizMode === 'both' || billingMode.includes('monthly'));
    const showDues = !isWalkin && billingMode.includes('pay_later');
    const current = order.f_payment_status;

    const options = [
        { key: 'due',            value: 'due',           colorKey: 'warning', icon: <AccessTime />,    label: 'Due',                 sub: 'Outstanding — appears in Dues'   },
        { key: 'paid_upi',       value: 'paid_directly', method: 'UPI',   colorKey: 'success', icon: <CurrencyRupee />, label: 'Paid — UPI',   sub: 'UPI collected'   },
        { key: 'paid_cash',      value: 'paid_directly', method: 'Cash',  colorKey: 'success', icon: <CurrencyRupee />, label: 'Paid — Cash',  sub: 'Cash collected'  },
        { key: 'paid_other',     value: 'paid_directly', method: 'Other', colorKey: 'success', icon: <CurrencyRupee />, label: 'Paid — Other', sub: 'Other method'    },
        ...(showDues    ? [{ key: 'pay_later', value: 'pay_later', colorKey: 'grey',    icon: <AccessTime />, label: 'Move to Dues Page',    sub: 'Customer will pay later'         }] : []),
        ...(showBilling ? [{ key: 'invoice',   value: 'invoice',   colorKey: 'primary', icon: <Receipt />,    label: 'Move to Monthly Bill', sub: 'Included when bill is generated' }] : []),
    ].filter(opt => opt.value !== current);

    return (
        <BottomSheet open onClose={onClose} title="Payment Status"
            subtitle={`Total: ${fmt(order.f_total_amount)}`}
        >
            {options.map(opt => (
                <SheetOption
                    key={opt.key}
                    colorKey={opt.colorKey}
                    icon={opt.icon}
                    label={opt.label}
                    sub={opt.sub}
                    disabled={loading}
                    onClick={() => onSelect(order, opt.value, opt.method)}
                />
            ))}
            <Button variant="text" fullWidth onClick={onClose} disabled={loading}
                sx={{ mt: 0.5, color: 'text.secondary', textTransform: 'none' }}>
                Cancel
            </Button>
        </BottomSheet>
    );
};

export default OrdersPage;
